import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Link2, Plus, Trash2, Lightbulb, ChevronDown, ChevronRight, Merge, AlertTriangle, MessageSquare } from 'lucide-react';

const ESTADO_COLORS = {
  ABIERTO: 'bg-blue-100 text-blue-800',
  EN_INVESTIGACION: 'bg-indigo-100 text-indigo-800',
  CERRADO: 'bg-green-100 text-green-800',
  CERRADO_POR_VINCULACION: 'bg-slate-200 text-slate-600',
  ESCALADO: 'bg-purple-100 text-purple-800',
  ESPERANDO_COMPLIANCE: 'bg-amber-100 text-amber-800',
  BLOQUEO_PREVENTIVO: 'bg-red-100 text-red-800',
  EN_VIGILANCIA: 'bg-cyan-100 text-cyan-800',
};

export const CaseLinksPanel = ({ caseId, onNavigate, onCaseUpdate }) => {
  const [links, setLinks] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const [linkedCaseId, setLinkedCaseId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedLink, setExpandedLink] = useState(null);

  useEffect(() => {
    if (caseId) { loadLinks(); loadSuggestions(); }
  }, [caseId]);

  const loadLinks = async () => {
    try {
      const res = await api.get(`/cases/${caseId}/links`);
      setLinks(res.data);
    } catch {} finally { setLoading(false); }
  };

  const loadSuggestions = async () => {
    try {
      const res = await api.get(`/cases/${caseId}/suggested-links`);
      setSuggestions(res.data);
    } catch {}
  };

  const addLink = async (targetId, reason) => {
    try {
      await api.post(`/cases/${caseId}/links`, { linked_case_id: targetId, motivo: reason || 'Relacion manual' });
      toast.success('Caso vinculado');
      loadLinks(); loadSuggestions();
      setShowAddDialog(false); setLinkedCaseId(''); setMotivo('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al vincular'); }
  };

  const removeLink = async (linkId) => {
    if (!window.confirm('¿Eliminar este vínculo?')) return;
    try {
      await api.delete(`/cases/${caseId}/links/${linkId}`);
      toast.success('Vínculo eliminado');
      loadLinks();
    } catch { toast.error('Error al eliminar vínculo'); }
  };

  const handleMerge = async () => {
    if (!mergeTarget) return;
    setMerging(true);
    try {
      const res = await api.post(`/cases/${caseId}/merge`, { absorbed_case_id: mergeTarget.caso_id });
      toast.success(`Caso unificado. ${mergeTarget.titulo || mergeTarget.caso_id.substring(0, 12)} cerrado por vinculación.`);
      setShowMergeDialog(false);
      setMergeTarget(null);
      loadLinks();
      if (onCaseUpdate) onCaseUpdate(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al unificar'); }
    finally { setMerging(false); }
  };

  if (loading) return null;

  return (
    <>
      <Card className="p-4 border border-slate-200" data-testid="case-links-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Link2 className="h-4 w-4" /> Casos Vinculados
            {links.length > 0 && <Badge variant="secondary" className="text-xs">{links.length}</Badge>}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowAddDialog(true)} data-testid="add-link-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Vincular
          </Button>
        </div>

        {links.length === 0 && suggestions.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-2">Sin casos vinculados</p>
        )}

        {links.map(link => {
          const isExpanded = expandedLink === link.link_id;
          const isCerrado = link.estado === 'CERRADO_POR_VINCULACION';
          return (
            <div key={link.link_id} className="border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-2 py-2 group">
                <button onClick={() => setExpandedLink(isExpanded ? null : link.link_id)} className="text-slate-400 hover:text-slate-600 p-0.5">
                  {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => onNavigate && onNavigate(link.caso_id)}
                    className="text-sm text-blue-600 hover:underline truncate block text-left"
                    data-testid={`link-navigate-${link.caso_id}`}
                  >
                    {link.titulo || link.caso_id.substring(0, 12)}
                  </button>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-[10px] px-1.5 py-0 ${ESTADO_COLORS[link.estado] || 'bg-gray-100'}`}>
                      {link.estado?.replace(/_/g, ' ') || '?'}
                    </Badge>
                    {link.nombre_padron && <span className="text-[10px] text-slate-500">{link.nombre_padron}</span>}
                    {link.motivo && <span className="text-[10px] text-slate-400 italic">{link.motivo}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!isCerrado && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Unificar (absorber este caso)"
                      onClick={() => { setMergeTarget(link); setShowMergeDialog(true); }} data-testid={`merge-link-${link.caso_id}`}>
                      <Merge className="h-3 w-3 text-blue-500" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Desvincular"
                    onClick={() => removeLink(link.link_id)} data-testid={`remove-link-${link.link_id}`}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </div>

              {/* Mini-preview expanded */}
              {isExpanded && (
                <div className="ml-7 mb-2 p-2 bg-slate-50 rounded-lg border border-slate-100 text-xs space-y-1">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-600">
                    {link.prioridad && <div>Prioridad: <strong>{link.prioridad}</strong></div>}
                    {link.total_alertas > 0 && <div>Alertas: <strong>{link.total_alertas}</strong></div>}
                    {link.score_riesgo > 0 && <div>Score: <strong>{link.score_riesgo}/100</strong></div>}
                    {link.cuit && <div>CUIT: <strong>{link.cuit}</strong></div>}
                  </div>
                  {link.motivo && (
                    <div className="flex items-center gap-1 text-[10px] text-slate-500 pt-1 border-t border-slate-100">
                      <MessageSquare size={10} /> {link.motivo}
                    </div>
                  )}
                  <div className="flex gap-1.5 pt-1">
                    <Button variant="outline" size="sm" className="h-5 text-[10px] px-2"
                      onClick={() => onNavigate && onNavigate(link.caso_id)}>
                      Abrir caso
                    </Button>
                    {!isCerrado && (
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => { setMergeTarget(link); setShowMergeDialog(true); }}>
                        <Merge size={10} className="mr-1" /> Unificar aqui
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
            <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
              <Lightbulb className="h-3 w-3 text-amber-500" /> Posibles relaciones
            </p>
            {suggestions.slice(0, 3).map(s => (
              <div key={s.id} className="flex items-center gap-2 py-1.5">
                <div className="flex-1 min-w-0">
                  <button onClick={() => onNavigate && onNavigate(s.id)} className="text-xs text-blue-600 hover:underline truncate block text-left">
                    {s.titulo || s.id.substring(0, 12)}
                  </button>
                  <Badge className={`text-[10px] px-1.5 py-0 ${ESTADO_COLORS[s.estado] || 'bg-gray-100'}`}>
                    {s.estado}
                  </Badge>
                </div>
                <Button variant="outline" size="sm" className="h-6 text-xs"
                  onClick={() => addLink(s.id, 'Sugerencia automatica')} data-testid={`suggest-link-${s.id}`}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add link dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Vincular caso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm text-slate-600">ID del caso</label><Input value={linkedCaseId} onChange={e => setLinkedCaseId(e.target.value)} placeholder="UUID del caso" data-testid="link-case-id-input" /></div>
            <div><label className="text-sm text-slate-600">Motivo</label><Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: Mismo titular, operaciones cruzadas" data-testid="link-motivo-input" /></div>
            <Button onClick={() => addLink(linkedCaseId, motivo)} disabled={!linkedCaseId} className="w-full" data-testid="link-confirm-btn">Vincular</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge confirmation dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Merge size={18} className="text-blue-600" /> Unificar caso</DialogTitle>
            <DialogDescription>
              Se absorberán las alertas, movimientos, comentarios y cuentas del caso seleccionado al caso actual. El caso absorbido se cerrará con estado "CERRADO POR VINCULACION".
            </DialogDescription>
          </DialogHeader>
          {mergeTarget && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                <span className="text-sm font-medium text-amber-800">Esta accion no se puede deshacer</span>
              </div>
              <p className="text-xs text-amber-700">
                El caso <strong>{mergeTarget.titulo || mergeTarget.caso_id?.substring(0, 12)}</strong> será cerrado permanentemente y sus datos se moverán al caso actual.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>Cancelar</Button>
            <Button onClick={handleMerge} disabled={merging} className="bg-blue-600 hover:bg-blue-700">
              {merging ? 'Unificando...' : 'Confirmar unificacion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
